import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Shield, Database, Settings, CreditCard, ShieldCheck, Cookie, Share2, UserCog, RefreshCw } from "lucide-react";

const siteUrl = "https://www.sevaafoundation.com";
const pageUrl = `${siteUrl}/privacy-policy`;
const defaultImage = `${siteUrl}/og-blogs.jpg`;
const logoImage = `${siteUrl}/logo512.png`;

const pageTitle = "Privacy Policy | SEVAA Foundation";
const pageDescription =
  "Read SEVAA Foundation's privacy policy to understand how personal data, donation details, cookies and payment information are collected and protected.";

const webPageSchema = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "@id": `${pageUrl}#webpage`,
  name: pageTitle,
  url: pageUrl,
  inLanguage: "en-IN",
  description: pageDescription,
  isAccessibleForFree: true,
  isPartOf: {
    "@type": "WebSite",
    "@id": `${siteUrl}/#website`,
    name: "SEVAA Foundation",
    url: siteUrl,
  },
  publisher: {
    "@type": "NGO",
    "@id": `${siteUrl}/#ngo`,
    name: "SEVAA Foundation",
    alternateName: "Swadesh Sevaa Charitable Foundation",
    url: siteUrl,
    logo: {
      "@type": "ImageObject",
      url: logoImage,
    },
  },
  about: [
    "Privacy Policy",
    "Data Protection",
    "Payment Information",
    "Cookies",
    "Personal Data",
    "Donor Privacy"
  ],
  primaryImageOfPage: {
    "@type": "ImageObject",
    url: defaultImage,
    width: 1200,
    height: 630,
  },
};

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "@id": `${pageUrl}#breadcrumb`,
  itemListElement: [
    {
      "@type": "ListItem",
      position: 1,
      name: "Home",
      item: `${siteUrl}/`,
    },
    {
      "@type": "ListItem",
      position: 2,
      name: "Privacy Policy",
      item: pageUrl,
    },
  ],
};


const PrivacyPolicy = () => {
  // Data array for the privacy policy to keep the code clean and manageable
  const privacyData = [
    {
      id: 1,
      title: "Information We Collect",
      icon: <Database className="w-6 h-6 text-teal-600" />,
      content: "We may collect the following information when you interact with our website:",
      list: [
        "Name and contact details",
        "Email address and phone number",
        "Donation details and transaction information",
        "Any information voluntarily submitted through forms"
      ]
    },
    {
      id: 2,
      title: "How We Use Information",
      icon: <Settings className="w-6 h-6 text-teal-600" />,
      content: "Your information may be used to:",
      list: [
        "Process donations",
        "Communicate updates about our work and initiatives",
        "Respond to queries and support requests",
        "Maintain transparency and reporting requirements"
      ],
      extraNote: "We do not sell, trade, or rent personal information to third parties."
    },
    {
      id: 3,
      title: "Payment Information",
      icon: <CreditCard className="w-6 h-6 text-teal-600" />,
      content: "Online payments are processed through secure third-party payment gateways. SEVAA does not store your full payment card details."
    },
    {
      id: 4,
      title: "Data Protection",
      icon: <ShieldCheck className="w-6 h-6 text-teal-600" />,
      content: "We implement reasonable security measures to protect personal information from unauthorized access, misuse, or disclosure."
    },
    {
      id: 5,
      title: "Cookies",
      icon: <Cookie className="w-6 h-6 text-teal-600" />,
      content: "Our website may use cookies to improve user experience and understand website usage patterns."
    },
    {
      id: 6,
      title: "Third-Party Sharing",
      icon: <Share2 className="w-6 h-6 text-teal-600" />,
      content: "Information may be shared only when necessary:",
      list: [
        "With payment processors",
        "To comply with legal requirements",
        "For transparency in regulatory reporting"
      ]
    },
    {
      id: 7,
      title: "Your Rights",
      icon: <UserCog className="w-6 h-6 text-teal-600" />,
      content: "You may request access, correction, or removal of your personal data by contacting us."
    },
    {
      id: 8,
      title: "Updates to Policy",
      icon: <RefreshCw className="w-6 h-6 text-teal-600" />,
      content: "We may update this Privacy Policy periodically. Continued use of the website indicates acceptance of updates."
    }
  ];

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="title" content={pageTitle} />
        <meta name="description" content={pageDescription} />
        <meta name="robots" content="index, follow, max-image-preview:large" />
        <meta name="author" content="SEVAA Foundation" />
        <meta name="theme-color" content="#0f766e" />
        <meta name="language" content="English, Hindi" />

        <link rel="canonical" href={pageUrl} />

        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="SEVAA Foundation" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:image" content={defaultImage} />
        <meta property="og:image:alt" content="SEVAA Foundation privacy policy and data protection" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:locale" content="en_IN" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={defaultImage} />
        <meta name="twitter:image:alt" content="SEVAA Foundation privacy policy and data protection" />

        <script type="application/ld+json">
          {JSON.stringify(webPageSchema)}
        </script>

        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Helmet>

    <div className="bg-slate-50 min-h-screen font-poppins pb-20">
      
      {/* 1. HERO SECTION */}
      <div className="relative w-full h-[400px] bg-teal-900 overflow-hidden">
        {/* Abstract Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 -left-40 w-96 h-96 bg-teal-500 rounded-full mix-blend-multiply filter blur-3xl opacity-70"></div>
          <div className="absolute top-0 -right-40 w-96 h-96 bg-orange-500 rounded-full mix-blend-multiply filter blur-3xl opacity-70"></div>
          <div className="absolute -bottom-40 left-20 w-96 h-96 bg-teal-300 rounded-full mix-blend-multiply filter blur-3xl opacity-70"></div>
        </div>

        {/* Hero Content */}
        <div className="relative h-full max-w-4xl mx-auto px-6 flex flex-col items-center justify-center text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="w-16 h-16 bg-teal-800 rounded-2xl border border-teal-700 flex items-center justify-center mb-6 shadow-xl"
          >
            <Shield className="w-8 h-8 text-orange-400" />
          </motion.div>
          
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-4xl md:text-5xl font-black text-white mb-4 tracking-tight"
          >
            Privacy Policy
          </motion.h1>
          
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-teal-100 text-base md:text-lg max-w-2xl font-medium"
          >
            SEVAA respects your privacy and is committed to protecting the personal information shared through this website.
          </motion.p>
        </div>
      </div>

      {/* 2. CONTENT SECTION */}
      <div className="max-w-7xl mx-auto px-6 -mt-10 relative z-10">
        <div className="bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-100 p-8 md:p-12">
          
          <p className="text-slate-500 mb-10 text-sm font-medium border-b border-slate-100 pb-6 leading-relaxed">
            This Privacy Policy explains how we collect, use, and protect your data when you interact with our platform. Your trust is our top priority.
          </p>

          <div className="space-y-12">
            {privacyData.map((policy, index) => (
              <motion.div 
                key={policy.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                className="flex flex-col md:flex-row gap-4 md:gap-6 group"
              >
                {/* Icon Column */}
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 rounded-xl bg-teal-50 border border-teal-100 flex items-center justify-center group-hover:bg-teal-600 group-hover:text-white transition-colors duration-300">
                    {React.cloneElement(policy.icon, { className: "w-6 h-6 text-teal-600 group-hover:text-white transition-colors duration-300" })}
                  </div>
                </div>

                {/* Text Column */}
                <div>
                  <h2 className="text-xl font-bold text-slate-900 mb-3 flex items-center gap-2">
                    <span className="text-slate-300 font-black text-lg">{policy.id}.</span> 
                    {policy.title}
                  </h2>
                  <p className="text-slate-600 leading-relaxed font-medium">
                    {policy.content}
                  </p>
                  
                  {/* Render list if it exists */}
                  {policy.list && (
                    <ul className="mt-4 space-y-2">
                      {policy.list.map((item, i) => (
                        <li key={i} className="flex items-start gap-3">
                          <span className="w-1.5 h-1.5 rounded-full bg-orange-400 mt-2.5 flex-shrink-0"></span>
                          <span className="text-slate-600 font-medium">{item}</span>
                        </li>
                      ))}
                    </ul>
                  )}

                  {/* Render extra note if it exists (for Section 2) */}
                  {policy.extraNote && (
                    <div className="mt-6 bg-orange-50 border-l-4 border-orange-500 p-4 rounded-r-lg">
                      <p className="text-slate-800 font-bold text-sm">
                        {policy.extraNote}
                      </p>
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </div>

        </div>
      </div>

      </div>
    </>
  );
};

export default PrivacyPolicy;
